import { Form, message } from 'antd';
import React, {
  createContext,
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { FarmData } from 'types/FarmData';
import useQueryApiClient from '../utils/useQueryApiClient';
import { useForm } from 'antd/es/form/Form';
import { AxiosResponse } from 'axios';
import readHeader from '../utils/readHeader';

export interface AuthProps {
  name?: string;
  surname?: string;
  authenticated: boolean;
  farmId?: number;
  isAdmin?: boolean;
  isFarmOwner?: boolean;
  isMansLauksLinked?: boolean;
  regNr?: number;
  email?: string;
  abilities?: Ability[];
  farmName?: string;
  role?: string;
  farm?: FarmData;
}

interface ErrorResponse {
  error: string;
}

export type Ability =
  | 'farm_edit'
  | 'admin_panel'
  | 'farm_delete'
  | 'farm_add'
  | 'user_add'
  | 'user_edit'
  | 'user_roles_edit'
  | 'user_delete'
  | 'roles_add'
  | 'roles_delete'
  | 'reports'
  | 'profile_panel';

interface AuthContextProps {
  user: AuthProps | null;
  isFetchingUser: boolean;
  login: (formData: any) => Promise<void>;
  logout: () => Promise<void>;
  refetchUser: () => void;
  isLoginLoading: boolean;
  atuhenticate: (userData: AuthProps) => void;
  farms: FarmData[];
  refetchFarms: () => void;
  setIsOpenModal: Dispatch<SetStateAction<boolean>>;
  isOpenModal: boolean;
  resendEmail: any;
  cooldown: number;
  pendingEmail: string | null;
}

const AuthContext = createContext<AuthContextProps | undefined>(undefined);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [pendingEmail, setPendingEmail] = useState<string | null>(null);
  const [cooldown, setCooldown] = useState(0);
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [user, setUser] = useState<AuthProps | null>(null);
  const [isFetchingUser, setIsFetchingUser] = useState<boolean>(true);
  const navigate = useNavigate();
  const comesFrom = localStorage.getItem('comesFrom');
  const intl = useIntl();
  const [form] = useForm();

  useEffect(() => {
    const until = Number(sessionStorage.getItem('cdUntil'));
    if (until && until > Date.now()) {
      setCooldown(Math.ceil((until - Date.now()) / 1000));
    }
  }, []);

  useEffect(() => {
    if (cooldown <= 0) return;

    const id = setInterval(() => {
      setCooldown((sec) => {
        if (sec <= 1) {
          clearInterval(id);
          return 0;
        }
        return sec - 1;
      });
    }, 1000);

    return () => clearInterval(id);
  }, [cooldown]);

  const { data: farms, refetch: refetchFarms } = useQueryApiClient({
    request: {
      url: `/api/v2/user/farms`,
    },
    enabled: !!user?.authenticated,
  });

  const { refetch: fetchUser } = useQueryApiClient({
    request: {
      url: `/api/v2/user`,
      method: 'GET',
    },
    onSuccess: (data: AuthProps) => {
      setUser(data);
      setIsFetchingUser(false);
    },
    onError: () => {
      setUser({ authenticated: false });
      setIsFetchingUser(false);
      sessionStorage.removeItem('processPurchaseFormValues');
    },
    enabled: true,
  });

  const { appendData } = useQueryApiClient({
    request: {
      url: `/api/v2/login`,
      method: 'POST',
    },
    onSuccess: (response) => {
      setUser({ authenticated: response.authenticated } as AuthProps);
      fetchUser();
      if (comesFrom) {
        window.location.href = `/${comesFrom}`;
        localStorage.removeItem('comesFrom');
      } else {
        window.location.href = `/`;
      }
    },
    onError: (response) => {
      handleErrorResponse(response);
    },
    enabled: true,
  });

  const { appendData: logoutRequest, isLoading: isLoginLoading } =
    useQueryApiClient({
      request: {
        url: `/api/v2/logout`,
        method: 'POST',
      },
      onFinally: () => {
        setUser({ authenticated: false });
        window.location.href = '/login';
        sessionStorage.removeItem('processPurchaseFormValues');
      },
      enabled: true,
    });

  const { appendData: resendEmail } = useQueryApiClient({
    request: {
      url: '/api/v2/resend-email',
      method: 'POST',
      errorWithHeaders: true,
    },
    onSuccess: () => handleSuccessResend(),
    onError: (response: AxiosResponse) => handleResendEmailError(response),
  });

  const login = async (formData: any) => {
    try {
      await fetch(`${window.runConfig.backendUrl}/sanctum/csrf-cookie`, {
        credentials: 'include',
      });
      setPendingEmail(formData.email);
      appendData(formData);
    } catch (error) {
      console.error('Login error:', error);
    }
  };

  const logout = async () => {
    try {
      logoutRequest();
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const handleErrorResponse = (response: ErrorResponse) => {
    if (response.error === 'verify_email') {
      setIsOpenModal(true);
      message.error(intl.formatMessage({ id: `message.${response.error}` }));
    } else {
      message.error(intl.formatMessage({ id: `message.${response.error}` }));
    }
  };

  const handleSuccessResend = () => {
    setPendingEmail(null);
    setIsOpenModal(false);
    message.success(intl.formatMessage({ id: 'login.resend_success' }));
  };

  const handleResendEmailError = (res: AxiosResponse) => {
    const { status, data } = res;

    switch (status) {
      case 429: {
        const retryAfter = Number(readHeader(res, 'retry-after') ?? 60);
        const until = Date.now() + retryAfter * 1000;

        sessionStorage.setItem('cdUntil', String(until));
        setCooldown(retryAfter);

        message.warning(
          intl.formatMessage({ id: 'login.email_already_sent_msg' })
        );
        break;
      }
      case 404: {
        message.error(intl.formatMessage({ id: `login.${data.error}` }));
        break;
      }
      case 409: {
        if (data?.message === 'already_verified') {
          message.info(intl.formatMessage({ id: `login.${data.message}` }));
        }
        break;
      }
      default: {
        const msgId = data?.error ?? 'message.unknown_error';
        message.error(intl.formatMessage({ id: msgId }));
      }
    }
  };

  const refetchUser = () => fetchUser();

  const atuhenticate = (userData: AuthProps) => {
    setUser(userData);
    fetchUser();
  };

  const handleEmailResend = async (values: any) => {
    try {
      await form.validateFields();

      resendEmail({ email: values.email });

      setIsOpenModal(false);

      message.success(intl.formatMessage({ id: 'login.resend_success' }));
    } catch (e) {}
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isFetchingUser,
        login,
        logout,
        refetchUser,
        isLoginLoading,
        atuhenticate,
        farms,
        refetchFarms,
        setIsOpenModal,
        isOpenModal,
        resendEmail,
        pendingEmail,
        cooldown,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
